import React from "react";
import styles from "../../../../styles/AdminConsole.module.css";

/**
 * DocChips
 *
 * Document chip list + R2 file upload + manual URL entry.
 * Used in Quality's Add Visit and Edit Visit modals.
 * Promotes the existing renderDocChips render-function to a proper
 * memoised component.
 *
 * Props:
 *   items         string[]         — current list of URLs or doc names
 *   inputValue    string           — controlled text input value
 *   onInputChange (val: string) => void
 *   onAdd         () => void       — called when Enter/Tab/+ is triggered
 *   onRemove      (item: string) => void
 *   onFileUpload  (e: ChangeEvent) => void  — handles the native file input change
 *   uploading     bool             — disables and dims the upload button while active
 *
 * Usage:
 *   <DocChips
 *     items={visitForm.documents}
 *     inputValue={visitDocInput}
 *     onInputChange={setVisitDocInput}
 *     onAdd={handleAddDoc}
 *     onRemove={handleRemoveDoc}
 *     onFileUpload={handleFileUpload}
 *     uploading={uploadingFiles}
 *   />
 */
const DocChips = React.memo(function DocChips({
  items,
  inputValue,
  onInputChange,
  onAdd,
  onRemove,
  onFileUpload,
  uploading,
}) {
  return (
    <div>
      {/* Chip list */}
      <div
        style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 6 }}
      >
        {items.map((d) => {
          const isUrl = d.startsWith("http");
          const label = isUrl ? d.split("/").pop() : d;
          return (
            <span
              key={d}
              style={{
                background: "rgba(100,200,160,0.10)",
                border: "1px solid rgba(100,200,160,0.22)",
                borderRadius: 4,
                padding: "3px 8px",
                fontSize: 10,
                display: "flex",
                alignItems: "center",
                gap: 5,
                color: "var(--text-body)",
              }}
            >
              {isUrl ? (
                <a
                  href={d}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: "rgba(100,200,160,0.9)",
                    textDecoration: "none",
                  }}
                >
                  📄 {label}
                </a>
              ) : (
                <span>📄 {label}</span>
              )}
              <button
                onClick={() => onRemove(d)}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  color: "rgba(100,200,160,0.7)",
                  fontSize: 12,
                  padding: 0,
                  lineHeight: 1,
                }}
              >
                ×
              </button>
            </span>
          );
        })}
      </div>

      {/* File upload */}
      <label
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          cursor: uploading ? "not-allowed" : "pointer",
          fontSize: 11,
          color: "rgba(100,200,160,0.85)",
          marginBottom: 6,
          opacity: uploading ? 0.6 : 1,
        }}
      >
        <input
          type="file"
          multiple
          style={{ display: "none" }}
          disabled={uploading}
          onChange={onFileUpload}
        />
        {uploading ? "Uploading…" : "⬆ Upload files"}
      </label>

      {/* Manual URL / name entry */}
      <div style={{ display: "flex", gap: 6 }}>
        <input
          className={styles.input}
          style={{ flex: 1 }}
          placeholder="Or paste a URL / document name"
          value={inputValue}
          onChange={(e) => onInputChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === "Tab") {
              e.preventDefault();
              onAdd();
            }
          }}
        />
        <button
          className={`${styles.btn} ${styles.btnPrimary}`}
          style={{ padding: "6px 14px", fontSize: 11 }}
          onClick={onAdd}
        >
          +
        </button>
      </div>
    </div>
  );
});

export default DocChips;
